import React, { useEffect } from "react";
import { Route, useLocation, useNavigate } from "react-router-dom";
import ProtectedRoute from "helpers/ProtectedRoute";

import HomeMenu from "./components/views/HomeMenu";
import ListView from "./components/views/ListView";
import VideoView from "./components/views/VideoView";
import ContentDetail from "./components/views/ContentDetail";
import ProfilePage from "./components/views/ProfilePage";
import FormContent from "./components/views/FormContent";
import NotFound from "./components/views/NotFound";

const componentRegistry = {
  HomeMenu,
  ListView,
  VideoView,
  ContentDetail,
  ProfilePage,
  FormContent,
  NotFound
};

export function buildRouteTree(routes) {
  const routeMap = {};
  const tree = [];

  // initialize route map
  routes.forEach(r => {
    routeMap[r.path] = { ...r, children: [] };
  });

  // link children to parents
  routes.forEach(r => {
    if (r.parent) {
      const parent = routeMap[r.parent];
      if (parent) {
        parent.children.push(routeMap[r.path]);
      }
    } else {
      tree.push(routeMap[r.path]);
    }
  });

  // recursive sort by sibling_order
  function sortTree(nodes) {
    nodes.sort((a, b) => (a.sibling_order ?? 0) - (b.sibling_order ?? 0));
    nodes.forEach(child => sortTree(child.children));
  }

  sortTree(tree);
  return tree;
}

function joinPaths(base, child) {
  if (!base || base === "/") return `/${child.replace(/^\/+/, "")}`;
  return `${base.replace(/\/+$/, "")}/${child.replace(/^\/+/, "")}`;
}

export function renderRoutes(routeNodes, basePath = "", level = 0, skipRoot = true) {
  return routeNodes.flatMap((r) => {
    const Component =
      componentRegistry[r.component] || componentRegistry["NotFound"];

    const hasChildren = r.children && r.children.length > 0;
    const isLeaf = !hasChildren;

    const routePath = r.path.replace(/^\//, "");
    const fullPath = joinPaths(basePath, routePath);

    const routes = [];

    // Only skip "/" at root level
    const isRoot = level === 0 && r.path === "/";
    if (isRoot) {
      if (hasChildren) {
        // Render children as top-level routes
        routes.push(renderRoutes(r.children, basePath, level + 1, false));
      }
    } else {
      routes.push(
        <Route
          key={fullPath || "/"}
          path={routePath}
          element={
            <ProtectedRoute>
              <Component
                title={r.title}
                url={fullPath}
                table={isLeaf || r.has_content ? routePath : undefined}
                list={hasChildren ? r.children : undefined}
                sort_by={r.sort_by}
              />
            </ProtectedRoute>
          }
        >
          {hasChildren &&
            renderRoutes(r.children, fullPath, level + 1, false)}

          {(isLeaf || r.has_content) && (
            <Route
              path=":id"
              element={
                <ProtectedRoute>
                  {React.createElement(
                    componentRegistry["ContentDetail"] || componentRegistry["NotFound"],
                    { table: routePath }
                  )}
                </ProtectedRoute>
              }
            />
          )}
        </Route>
      );
    }

    return routes.flat();
  });
}

export function flattenPaths(routeNodes, base = "", list = []) {
  for (const node of routeNodes) {
    const cleanPath = node.path.replace(/^\//, "");
    var fullPath = base ? `${base}/${cleanPath}` : `/${cleanPath}`;

    // normalize slashes
    fullPath = fullPath.replace(/\/+/g, "/");

    // add main path
    list.push(fullPath);

    // add dynamic version (leaf nodes only)
    if (!node.children?.length) {
      const dynamic = `${fullPath}/:id`.replace(/\/+/g, "/");
      list.push(dynamic);
    }

    // continue walking tree
    if (node.children?.length > 0) {
      flattenPaths(node.children, fullPath, list);
    }
  }
  return list;
}

function matchesDynamicRoute(candidate, validPaths) {
  return validPaths.some(path => {
    const regex = new RegExp("^" + path.replace(/:\w+/g, "[^/]+") + "$");
    return regex.test(candidate);
  });
}

export function SmartFallbackRoute({ validPaths = [] }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);

    // progressively strip levels from deepest → shallowest
    while (parts.length > 0) {
      const candidate = "/" + parts.join("/");

      if (validPaths.includes(candidate)){
        return navigate(candidate, { replace: true });
      }

      if (matchesDynamicRoute(candidate, validPaths)){
        return navigate(candidate, { replace: true });
      }

      parts.pop();
    }

    // final fallback
    navigate("/home", { replace: true });

  }, [location, navigate, validPaths]);

  return null;
}
